import { Typography } from '@mui/material';
import { Card } from '@client/new/components/Card';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { useLocalStorage } from '@client/hooks/use-local-storage';
import React, { PropsWithChildren } from 'react';
import { InfoTip } from '../InfoTip';
import { LiteUser, useInfluencer } from '@client/new/common/LiteUser.mapper';
import { F0, F1, TMap } from '../../../helpers/types';
import { ConfigurableSourceType } from '../integrations';
import Spinner from '../../../../components/Spinner';
import { SourceType } from './useURLBuilderState';

export const StyledCard = (p: Required<React.PropsWithChildren>) => (
  <Card variant="basic" rounded="2xl" className="px-5 py-5 flex flex-col gap-4">
    {p.children}
  </Card>
);

export const InfoUtmParams = () => (
  <div
    className="rounded-2xl px-5 py-7 flex flex-col gap-4"
    style={{ background: '#F8F8F8' }}
  >
    <Typography variant="h6">Information and examples of parameters</Typography>

    <Typography
      variant="body2"
      className="grid gap-6 "
      component={'div'}
      style={{ gridTemplateColumns: '1fr max-value 1fr' }}
    >
      <div>
        Campaign Name
        <br />
        <b>utm_campaign</b>
      </div>
      <b>Winter_Sale</b>
      <span>
        Use <b>utm_campaign</b> to identify a specific product promotion or
        strategic campaign.
      </span>
      <hr className="col-span-3" />
      <div>
        Campaign Source
        <br />
        <b>utm_source</b>
      </div>
      <b>google</b>
      <span>
        Use <b>utm_source</b> to identify a website, social media app, email
        campaign or other source.
      </span>
    </Typography>
  </div>
);

const FakeLink = (p: PropsWithChildren<{ onClick: F0 }>) => (
  <span className="cursor-pointer underline" {...p}>
    {p.children}
  </span>
);

export type InfoGeneralProps = {
  onNavigateToIntegration: (type?: ConfigurableSourceType) => void;
};
export const InfoGeneral = (p: InfoGeneralProps) => {
  const [showInfo, setShowInfo] = useLocalStorage('urlBuilderInfoTip', true);
  if (!showInfo) return null;
  return (
    <StyledCard>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <FontAwesomeIcon icon={faInfoCircle} />
          <Typography variant="h5">
            What is the Marketing URL Builder?
          </Typography>
        </div>
      </div>
      <Typography variant="body2">
        Marketing URL Builder is a simple yet powerful tool that allows you to
        track the performance of your marketing campaigns, both paid and
        non-paid. Generate unique tracking URLs (or links) that can be added on
        social media (Instagram, TikTok, YouTube), or embedded inside email
        marketing campaigns and websites.
        <br />
        <b>
          Note: if you would like to track the performance of ads, make sure you
          have 3rd-party{' '}
          <FakeLink onClick={() => p.onNavigateToIntegration()}>
            integrations
          </FakeLink>
          &nbsp;set up first.
        </b>
      </Typography>
      <div
        onClick={() => setShowInfo(false)}
        className="self-end text-xs cursor-pointer text-gray-400"
      >
        {'hide'}
      </div>
    </StyledCard>
  );
};

const integrationNamesMap: TMap<ConfigurableSourceType, string> = {
  facebook: 'Meta Pixel', //1966960130343053,
  tiktok: 'TikTok Pixel', // 55f59933f2ea98ad46b8555ca68a345401732b22
  google: 'Google Services',
};

const serviceNamesMap: TMap<ConfigurableSourceType, string> = {
  facebook: 'Facebook or Instagram', //1966960130343053,
  tiktok: 'Tiktok', // 55f59933f2ea98ad46b8555ca68a345401732b22
  google: 'Google Services',
};

export const capitalize = (v: string) => v.charAt(0).toUpperCase() + v.slice(1);

export const IntegrationInfoTip = ({
  ...p
}: {
  source: SourceType | undefined;
  creator: LiteUser;
  onNavigateToIntegration: F1<ConfigurableSourceType>;
}) => {
  const asyncCreator = useInfluencer(p.creator.id);
  const source = p.source as ConfigurableSourceType;
  if (!integrationNamesMap[source]) return null;
  if (asyncCreator.type !== 'fetched') return <Spinner />;
  const creator = asyncCreator.value;
  const invalidMap: TMap<ConfigurableSourceType, boolean> = {
    facebook:
      !creator.facebookPixelId || !creator.facebookConversionApiAccessToken,
    tiktok:
      !creator.tikTokConfiguration ||
      !creator.tikTokConfiguration.pixelId ||
      !creator.tikTokConfiguration.apiSecret,
    google:
      !creator.ga4Configuration ||
      !creator.ga4Configuration.measurementId ||
      !creator.ga4Configuration.apiSecret,
  };
  if (!invalidMap[source]) return null;

  return (
    <div className="mt-[1em]">
      <InfoTip>
        {`If you intend to track Paid Ads on ${serviceNamesMap[source]} make sure
      you’ve set up the integration with `}
        <FakeLink onClick={() => p.onNavigateToIntegration(source)}>
          {integrationNamesMap[source]}
        </FakeLink>
      </InfoTip>
    </div>
  );
};
